import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { fetchReport, fetchReportChecklist } from "../../../../api/service/report";

function unwrapPayload(res) {
  const body = res?.data ?? res;
  if (body?.errors) return { __error: true, errors: body.errors };
  return body?.data ?? body;
}

function formatDate(raw, language) {
  if (!raw) return "";
  const key = String(raw).substring(0, 10);
  const date = new Date(`${key}T12:00:00`);
  if (Number.isNaN(date.getTime())) return key;
  try {
    return new Intl.DateTimeFormat(language || "nb-NO", {
      day: "numeric",
      month: "short",
      year: "numeric",
    }).format(date);
  } catch {
    return key;
  }
}

function checklistItems(checklist) {
  if (Array.isArray(checklist?.items)) return checklist.items;
  if (Array.isArray(checklist?.checklist?.items)) return checklist.checklist.items;
  return [];
}

/**
 * Read-only report drill-in inside the previous-work dialog.
 */
export function PreviousWorkReportPanel({ reportId }) {
  const { t, i18n } = useTranslation();
  const [report, setReport] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(false);

  useEffect(() => {
    if (!reportId) return undefined;
    let cancelled = false;
    setLoading(true);
    setError(false);
    fetchReport(reportId, {
      fields:
        "id,report_number,displayTitle,title,status,finished_date,order_date,date,description,customer,report_type",
    })
      .then((res) => {
        if (cancelled) return;
        const data = unwrapPayload(res);
        if (data?.__error || !data?.id) {
          setReport(null);
          setError(true);
        } else {
          setReport(data);
        }
      })
      .catch(() => {
        if (!cancelled) {
          setReport(null);
          setError(true);
        }
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [reportId]);

  if (loading) {
    return <p className="text-sm text-slate-500">{t("Loading...")}</p>;
  }
  if (error || !report) {
    return (
      <p className="text-sm text-slate-500">{t("report.previous_work.panel_load_error")}</p>
    );
  }

  const title =
    report.displayTitle ||
    report.title ||
    t("report.previous_work.fallback", { number: report.report_number || report.id });
  const dateLabel = formatDate(
    report.finished_date || report.order_date || report.date,
    i18n?.language
  );

  return (
    <article className="rpw-panel">
      <h3 className="rpw-panel-title">{title}</h3>
      <div className="rpw-panel-meta">
        {report.report_number ? <span>#{report.report_number}</span> : null}
        {dateLabel ? <span>{dateLabel}</span> : null}
        {report.status ? <span>{report.status}</span> : null}
        {report.report_type?.title || report.report_type?.displayTitle ? (
          <span>{report.report_type.title || report.report_type.displayTitle}</span>
        ) : null}
        {report.customer?.displayTitle ? <span>{report.customer.displayTitle}</span> : null}
      </div>
      {report.description ? <p className="rpw-panel-body">{report.description}</p> : null}
      <Link to={`/dashboard/service/reports/${report.id}`} className="rpw-panel-link">
        {t("report.previous_work.open_full_report")}
      </Link>
    </article>
  );
}

/**
 * Read-only checklist drill-in inside the previous-work dialog.
 */
export function PreviousWorkChecklistPanel({ reportId }) {
  const { t } = useTranslation();
  const [checklist, setChecklist] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(false);

  useEffect(() => {
    if (!reportId) return undefined;
    let cancelled = false;
    setLoading(true);
    setError(false);
    fetchReportChecklist(reportId, {
      fields:
        "id,title,description,status,items.id,items.title,items.description,items.isApproved,items.isDeviation,items.row_order",
    })
      .then((res) => {
        if (cancelled) return;
        if (res?.errors) {
          setChecklist(null);
          setError(true);
          return;
        }
        setChecklist(unwrapPayload(res));
      })
      .catch(() => {
        if (!cancelled) {
          setChecklist(null);
          setError(true);
        }
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [reportId]);

  if (loading) {
    return <p className="text-sm text-slate-500">{t("Loading...")}</p>;
  }
  if (error) {
    return (
      <p className="text-sm text-slate-500">{t("report.previous_work.panel_load_error")}</p>
    );
  }

  const items = checklistItems(checklist);
  if (!checklist?.id && items.length === 0) {
    return <p className="text-sm text-slate-500">{t("report.previous_work.no_checklist")}</p>;
  }

  return (
    <article className="rpw-panel">
      <h3 className="rpw-panel-title">
        {checklist?.title || t("report.previous_work.crumb_checklist", { number: reportId })}
      </h3>
      {checklist?.description ? (
        <p className="rpw-panel-body">{checklist.description}</p>
      ) : null}
      {items.length === 0 ? (
        <p className="mt-3 text-sm text-slate-500">{t("report.previous_work.no_checklist")}</p>
      ) : (
        <ul className="rpw-checklist">
          {items.map((item) => (
            <li key={item.id || item.title} className="rpw-checklist-item">
              <p className="rpw-checklist-item-title">{item.title || item.description}</p>
              {item.description && item.title ? (
                <p className="rpw-checklist-item-note">{item.description}</p>
              ) : null}
              {item.isDeviation ? (
                <span className="rpw-checklist-badge is-deviation">
                  {t("report.previous_work.checklist_deviation")}
                </span>
              ) : item.isApproved ? (
                <span className="rpw-checklist-badge is-ok">
                  {t("report.previous_work.checklist_ok")}
                </span>
              ) : null}
            </li>
          ))}
        </ul>
      )}
    </article>
  );
}
